
import Vue from 'vue';
import _sampleSize from 'lodash/sampleSize';
import _remove from 'lodash/remove';
import _debounce from 'lodash/debounce';
import _uniq from 'lodash/uniq';
import { mapState, mapActions } from 'vuex';
import { localPrompts } from '@/views/Runnits/utils/localData';
import { RunnitDynamicFieldMixin } from '@/views/Runnits/RunnitSettings/mixins/RunnitDynamicFieldMixin';
import RunnitSettingHelperBtn from '@/views/Runnits/RunnitSettings/RunnitSettingHelperBtn.vue';
import RunnitDynamicFieldLabelRow from '@/views/Runnits/RunnitSettings/RunnitDynamicFieldLabelRow.vue';
import PromptMagicButton from '@/views/Runnits/RunnitSettings/PromptMagicButton.vue';
import { getShowTextCounterAt, getTextMaxLengthRule } from '@/utils';
import _trim from 'lodash/trim';
import _truncate from 'lodash/truncate';
import ModelSuggestionMenu from './ModelSuggestionMenu.vue';
import { db, functions } from '@/firebase';
import {
	Model,
	Avatar,
	extractPromptStringValue,
	PROMPT_MAGIC_MODE,
	PromptMagicMode,
	RUNNIT_NODE_FIELD_TYPE,
	RunnitNodeFieldType,
	RunnitNodeField,
	RunnitNodeFieldDisplay_MODEL_SINGLE_SELECT,
	RunnitNodeRunResult,
	RunnitUpload,
} from '@run-diffusion/shared';
import MenuOrBottomSheet from '@/components/base/MenuOrBottomSheet.vue';
import ListItem from '@/components/base/ListItem.vue';
import { IMAGE_GALLERY_DIALOG_NAV, ImageGalleryDialogNav } from '@/components/ImageGallery/utils';
import { NODE_RUN_RESULT_SELECTION_MODE } from '@/views/Runnits/constants';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import ImageUpload from '@/views/Runnits/RunnitSettings/ImageInput/ImageUpload.vue';
import { IMAGE_UPLOAD_MODE } from '@run-diffusion/shared';
import { SNACKBAR_STATUS } from '@/constants/constants';

interface TextHistoryEntry {
	text: string;
	cursor: number;
}

export default Vue.extend({
	name: 'Prompt',
	mixins: [
		RunnitDynamicFieldMixin,
		RunnitsImageSelectMixin,
	],
	props: {
		value: { type: [String, Object], default: null },
		adminOnly: { type: Boolean, default: false },
		textareaOnly: { type: Boolean, default: false },
		showExpandToggle: { type: Boolean, default: false }, // works in conjunction with maxHeight to allow the prompt to expand
		editableContentStyles: { type: Object, default: () => ({}) },
	},
	data () {
		return {
			loadingPrompt: false,
			history: [] as TextHistoryEntry[],
			historyIndex: 0,
			maxHistoryLength: 50,
			isUndoingRedoing: false,
			debouncedAddToHistory: null,
			debouncedCheckForScroll: null,
			debouncedPlainTextExtraction: null,
			debouncedAccuracySync: null,
			hasScrollContent: false,
			nodeCache: null, // Cache for DOM nodes to avoid repeated traversals
			plainTextCache: { text: '', version: 0, lastContentLength: 0 }, // Cache for expensive plain text extraction

			lastPasteTextNode: null,

			// Compiled regex patterns for performance
			MODEL_ID_CHIP_REGEX: /@{MODEL-(\S{12,36})}/g,
			MODEL_ID_CHIP_REGEX_WITH_WHITESPACE: /(^|\s*)@{MODEL-(\S{12,25})}(\s*|$)/g,
			MODEL_ID_EXTRACT_REGEX: /@{MODEL-([^}]+)}/,

			modelMenuOpen: false,
			modelArchitecture: null,
			lastAtPosition: -1,
			menuPosition: { x: 0, y: 0 },
			localModelsMap: {} as Record<string, Model>,

			// Image selection menu
			IMAGE_GALLERY_DIALOG_NAV,
			IMAGE_UPLOAD_MODE,
			uploadMenuOpen: false,

			promptExpanded: false,
			placeholderMinHeight: null as number | null,
		};
	},
	beforeDestroy () {
		// Cancel any pending debounced calls when component is destroyed
		this.cancelDebouncedHistoryUpdate();
		this.cancelDebouncedScrollCheck();
		this.cancelDebouncedPlainTextExtraction();
		this.cancelDebouncedAccuracySync();
		// Remove selection change listeners
		if (this.$refs.editableContent) {
			const editableElement: HTMLElement = this.$refs.editableContent as HTMLElement;
			editableElement.removeEventListener('mouseup', this.handleSelectionChange);
			editableElement.removeEventListener('keyup', this.handleSelectionChange);
		}
	},
	mounted () {
		this.$nextTick(() => {
			if (this.$refs.editableContent) {
				const editableElement: HTMLElement = this.$refs.editableContent as HTMLElement;
				editableElement.addEventListener('mouseup', this.handleSelectionChange);
				editableElement.addEventListener('keyup', this.handleSelectionChange);

				// Set up scroll observer
				this.checkForScroll();
				const resizeObserver = new ResizeObserver(() => {
					this.checkForScroll();
				});
				resizeObserver.observe(editableElement);

				// Clean up observer on destroy
				this.$once('hook:beforeDestroy', () => {
					resizeObserver.disconnect();
				});
			}
			// Calculate placeholder height
			this.calculatePlaceholderHeight();
		});
	},
	computed: {
		...mapState([
			'runnitState',
		]),
		loading () {
			const isLoading = !!(this.loadingPrompt || this.$refs.imageUpload?.isUploading);
			return isLoading;
		},
		promptStringValue (): string {
			const value: string = extractPromptStringValue(this.value);
			const editableElement: HTMLElement = this.$refs.editableContent as HTMLElement;
			if (editableElement) {
				editableElement.classList.toggle('is-empty', !_trim(value));
			}
			return value;
		},

		noMagic () {
			return !!this._get(this.field, 'display.noMagic');
		},
		maxLength () {
			return this._get(this.field, 'display.maxLength') || this.DEFAULT_TEXT_MAX_LENGTH;
		},
		customMaxLengthRequiredTextField () {
			return [
				getTextMaxLengthRule(this.maxLength),
				v => !!_trim(v) || 'Required',
			];
		},
		customMaxLengthOptionalTextField () {
			return [
				getTextMaxLengthRule(this.maxLength),
			];
		},
		promptImageSelectionState () {
			return this.getSelectionStateByTrigger('prompt-image-selection');
		},
		enableModelSuggestions () {
			return !!(this.modelArchitecture && this.runnitState?.fieldsByType?.[RUNNIT_NODE_FIELD_TYPE.MODEL_SINGLE_SELECT]?.length);
		},
	},
	watch: {
		'runnitState.fieldsByType': {
			immediate: true,
			async handler (newVal: Record<RunnitNodeFieldType, RunnitNodeField[]>, oldVal: Record<RunnitNodeFieldType, RunnitNodeField[]>) {
				if (newVal !== oldVal) {
					this.modelArchitecture = null;
					(newVal?.[RUNNIT_NODE_FIELD_TYPE.MODEL_SINGLE_SELECT] || []).some((field: RunnitNodeField) => {
						this.modelArchitecture = (field.display as RunnitNodeFieldDisplay_MODEL_SINGLE_SELECT)?.architecture || null;
						return this.modelArchitecture;
					});
				}
			},
		},
		loading: {
			handler (newVal: boolean, oldVal: boolean) {
				if (newVal !== oldVal) {
					this.$emit('loading-changed', newVal);
				}
			},
			immediate: true,
		},
		promptStringValue: {
			immediate: true,
			handler (newVal: string, oldVal: string) {
				if (newVal !== oldVal) {
					// Add input value to history
					this.onDebouncedAddToHistory(newVal);

					const plainText: string = this.getCachedPlainText();

					/*
					Extract all tokens from both values - reuse compiled regex
					*/
					// Reset regex state before using
					this.MODEL_ID_CHIP_REGEX.lastIndex = 0;
					let tokensInNewVal: string[] = (newVal || '').match(this.MODEL_ID_CHIP_REGEX) || [];
					this.MODEL_ID_CHIP_REGEX.lastIndex = 0;
					const tokensInOldVal: string[] = (oldVal || '').match(this.MODEL_ID_CHIP_REGEX) || [];
					let hasTokenChanges: boolean =
						tokensInNewVal.length !== tokensInOldVal.length ||
						tokensInNewVal.some((token: string, i: number) => token !== tokensInOldVal[i]) ||
						tokensInOldVal.some((token: string, i: number) => token !== tokensInNewVal[i]);

					const shouldUpdateEditableContent: boolean = !!(
						this.lastPasteTextNode ||
						hasTokenChanges ||
						newVal !== plainText
					);

					if (!shouldUpdateEditableContent) {
						return;
					}
					// Check if this is paste operation with model token
					let modelIdFromPaste: string | null = null;
					if (this.lastPasteTextNode && this.enableModelSuggestions) {
						const pastedText: string = this.lastPasteTextNode.textContent || '';
						const modelMatch: RegExpMatchArray | null = pastedText.match(this.MODEL_ID_EXTRACT_REGEX);

						if (modelMatch && modelMatch.length > 1) {
							modelIdFromPaste = modelMatch[1];
						}
					}

					// Clear model selection in the form of no model IDs found
					if (!tokensInNewVal.length) {
						this.incrementPromptSelectedModelChangeTrigger({ model: null });
					}

					// Check for any model IDs that we don't have in our map yet
					if (tokensInNewVal.length > 0) {
						// Extract model IDs from tokens (e.g. @{MODEL-modelId} -> modelId)
						const modelIds: string[] = tokensInNewVal.map((token: string) => {
							const match: RegExpMatchArray | null = token.match(this.MODEL_ID_EXTRACT_REGEX);
							return match ? match[1] : null;
						}).filter(Boolean) as string[];

						// Find models that aren't in our map yet
						const missingModelIds: string[] = modelIds.filter(
							(id: string) => id && !this.localModelsMap[id],
						);

						if (missingModelIds.length > 0) {
							// Fetch missing models, don't await and hang this handler up if it takes a while
							this.fetchMissingModels(missingModelIds).then(({ errorIds }) => {
								// Don't use newVal here, get the current value the component state instead to avoid race conditions that were happening
								let valueAfterFetchingModels: string = this.promptStringValue;

								// Prioritize pasted model ID, fallback to first valid ID in prompt for consistent model selection in the prompt value
								let firstNonErrorModelId: string = modelIdFromPaste;
								if (!firstNonErrorModelId) {
									firstNonErrorModelId = modelIds.find(id => !errorIds.includes(id)) || null;
									if (firstNonErrorModelId) {
										// Replace all existing model tokens with the new model token using the helper method
										valueAfterFetchingModels = this.replaceAllModelTokens(valueAfterFetchingModels, firstNonErrorModelId);
									}
								}

								// Remove unavailable model tokens from the value
								if (errorIds.length > 0) {
									errorIds.forEach((id: string) => {
										// Create a regex to match this specific model token
										const tokenRegex: RegExp = new RegExp(`@{MODEL-${id}}`, 'g');
										valueAfterFetchingModels = valueAfterFetchingModels.replace(tokenRegex, '');
									});
								}

								// Update model selection based on value changes: clear model if invalid paste,
								// set to pasted model if valid, or just update display if no significant changes
								const didValueChangeAfterFetchingModels: boolean = valueAfterFetchingModels !== this.promptStringValue;
								let shouldUpdateEditableContent: boolean = !oldVal;
								if (
									didValueChangeAfterFetchingModels &&
									firstNonErrorModelId &&
									(errorIds.includes(firstNonErrorModelId) || !this.localModelsMap[firstNonErrorModelId])
								) {
									this.incrementPromptSelectedModelChangeTrigger({ model: null });
								} else if (
									didValueChangeAfterFetchingModels &&
									firstNonErrorModelId &&
									this.localModelsMap[firstNonErrorModelId]
								) {
									this.incrementPromptSelectedModelChangeTrigger({ model: this.localModelsMap[firstNonErrorModelId] });
								} else {
									shouldUpdateEditableContent = true;
								}

								if (shouldUpdateEditableContent) {
									// With models fetched, update the editable content to add model names instead of placeholders
									this.updateEditableContentWithCursorPreservation(valueAfterFetchingModels);
								}
							});
						} else if (hasTokenChanges) {
							// Prioritize pasted model ID, fallback to first valid ID in prompt for consistent model selection in the prompt value
							let firstModelId: string = modelIdFromPaste;
							if (!firstModelId) {
								firstModelId = modelIds[0];
							}
							this.incrementPromptSelectedModelChangeTrigger({ model: this.localModelsMap[firstModelId] });
						}
					}

					this.$nextTick(() => {
						this.updateEditableContentWithCursorPreservation(newVal);

						// The last thing to do, reset the force update flag set from pasting operations
						this.lastPasteTextNode = null;
					});
				}
			}
		},
		'$store.state.triggers.promptSelectedModelChangeTrigger': {
			immediate: true,
			handler (newVal: { model: Model, trigger: number }, oldVal: { model: Model, trigger: number }) {
				if (this.enableModelSuggestions && newVal.trigger > 0 && newVal !== oldVal) {
					this.updatePromptTextWithNewModel(newVal.model);
				}
			},
		},
		promptImageSelectionState: {
			immediate: false,
			handler (newVal, oldVal) {
				const selectedAsset = this.getPromptSelectionStateAsset(newVal, oldVal);
				if (!selectedAsset) return;
				this.generatePromptFromImage(selectedAsset);
			},
		},
		'field.placeholder': {
			handler () {
				this.calculatePlaceholderHeight();
			},
			immediate: false,
		},
	},
	methods: {
		...mapActions([
			'incrementPromptSelectedModelChangeTrigger',
			'updateRunnitState',
			'updateSnackbar',
		]),
		getShowTextCounterAt,
		onPromptStringValueInput (value: string) {
			const hasNoMagicDisplaySetting: boolean = !!this._get(this.field, 'display.noMagic');
			const currentMagicMode: PromptMagicMode = this.value?.magicMode;

			// Determine magic mode based on field settings and current value
			let magicMode: PromptMagicMode = PROMPT_MAGIC_MODE.AUTO; // Default
			if (hasNoMagicDisplaySetting) magicMode = PROMPT_MAGIC_MODE.OFF;
			else if (currentMagicMode) magicMode = currentMagicMode;

			this.$emit('input', {
				value: value || null,
				magicMode,
			});
		},
		checkIsModelSuggestionMenuEditing () {
			// Check if the focus should be in the model suggestions menu's text field
			if (this.modelMenuOpen && this.$refs.modelMenu) {
				const modelMenu = this.$refs.modelMenu;
				if (modelMenu.$refs.editableChip) {
					if (document.activeElement !== modelMenu.$refs.editableChip) {
						modelMenu.$refs.editableChip?.focus();
					}
					return true;
				}
			}
			return false;
		},
		onContentEditableInput (event: InputEvent) {
			if (this.checkIsModelSuggestionMenuEditing()) return;

			const editableElement: HTMLElement = this.$refs.editableContent as HTMLElement;

			// Immediate empty state check using lightweight method for visual feedback
			const textContent = editableElement.textContent || '';
			const isEmpty = !textContent.trim();
			editableElement.classList.toggle('is-empty', isEmpty);

			// Get plain text with aggressive caching for the actual value processing
			const plainText: string = this.getCachedPlainText();

			// Only update if value actually changed to prevent cursor jumps
			if (plainText !== this.promptStringValue) {
				// Setup debounced function if not already done
				this.setupDebouncedPlainTextExtraction();
				// Call debounced function with plain text
				this.debouncedPlainTextExtraction(plainText);
			}

			// Setup and trigger debounced accuracy sync to ensure data accuracy after typing stops
			this.setupDebouncedAccuracySync();
			this.debouncedAccuracySync();

			// Check for scroll after content changes (debounced for performance)
			this.debouncedScrollCheck();
		},

		// Helper method to calculate the position value for a node
		calculateNodePositionValue (node: Node, index: number): { positionValue: number, hasPreFormattedTokens: boolean } {
			// Create regex for token detection
			const tokenRegex: RegExp = new RegExp(this.MODEL_ID_CHIP_REGEX.source, this.MODEL_ID_CHIP_REGEX.flags);
			const hasPreFormattedTokens: boolean = tokenRegex.test((node.nodeType === Node.TEXT_NODE && node.textContent) || '');

			// Calculate position increment based on node type
			let positionValue: number = 0;
			if (node.nodeType === Node.TEXT_NODE && hasPreFormattedTokens) {
				// Handle text nodes with tokens - replace tokens with single character
				positionValue = (node.textContent || '').replace(tokenRegex, '$').length;
			} else if (node.nodeType === Node.TEXT_NODE && !hasPreFormattedTokens && node.textContent?.length) {
				// Handle regular text nodes - use text length
				positionValue = node.textContent.length;
			} else if (node.nodeType === Node.ELEMENT_NODE) {
				// Handle element nodes
				if ((node as HTMLElement).classList?.contains('prompt-field-chip')) {
					// Chips count as 1 character each
					positionValue = 1;
				} else if (
					index > 0 &&
					(
						(node as HTMLElement).tagName === 'BR' ||
						(node as HTMLElement).tagName === 'DIV'
					)
				) {
					// BR/DIV elements count as 1 character each if not at the beginning
					positionValue = 1;
				}
			}

			return {
				positionValue,
				hasPreFormattedTokens,
			};
		},

		// Helper method to get the current cursor position
		getCurrentCursorPosition (): number {
			const editableElement: HTMLElement = this.$refs.editableContent as HTMLElement;
			if (!editableElement) return 0;

			const selection: Selection = window.getSelection();
			if (!selection || selection.rangeCount === 0) return 0;

			const range: Range = selection.getRangeAt(0);

			// If selection is not in our editable element, return 0
			if (!editableElement.contains(range.startContainer)) return 0;

			// Get all content nodes in proper order (text nodes, chips, line breaks)
			const allNodes: Node[] = this.collectPromptValueNodes(editableElement);

			// Calculate position by traversing nodes up to the cursor
			let position: number = 0;

			for (let i = 0; i < allNodes.length; i++) {
				const node: Node = allNodes[i];

				// Calculate position for current node
				const {
					positionValue,
					hasPreFormattedTokens,
				} = this.calculateNodePositionValue(node, i);
				position += positionValue;

				// Now check if this is the cursor node
				const isCursorNode: boolean = (node === range.startContainer) || (this.lastPasteTextNode && node === this.lastPasteTextNode);
				if (isCursorNode) {
					// The cursor should resolve to being at the end of this pasted text node
					if (!this.lastPasteTextNode && node.nodeType === Node.TEXT_NODE && !hasPreFormattedTokens) {
						// Add offset only for post-formatted text nodes
						position = position - positionValue + range.startOffset;
					}
					break;
				}
			}

			return position;
		},

		// Helper method to collect all nodes in proper order for cursor position calculations
		collectPromptValueNodes (element: HTMLElement): Node[] {
			// Simple cache invalidation: if content changed significantly, clear cache
			const contentLength = element.textContent?.length || 0;
			if (this.nodeCache && this.nodeCache.contentLength === contentLength && this.nodeCache.element === element) {
				return this.nodeCache.nodes;
			}

			// Get all nodes in document order
			const result: Node[] = [];

			// Use a custom function to traverse DOM instead of TreeWalker
			// to have more control over which nodes are included
			const traverseNodes = (parent: Node) => {
				// Process each child node
				for (let i = 0; i < parent.childNodes.length; i++) {
					const node: Node = parent.childNodes[i];

					// Handle element nodes
					if (node.nodeType === Node.ELEMENT_NODE) {
						const el: HTMLElement = node as HTMLElement;

						if (el.classList?.contains('prompt-field-chip') || el.tagName === 'BR') {
							result.push(node);
							continue; // Skip traversing inside chip
						} else if (el.tagName === 'DIV') {
							result.push(node);
						}

						// Traverse inside
						traverseNodes(node);
					}
					// Handle text nodes - always add them
					else if (node.nodeType === Node.TEXT_NODE) {
						result.push(node);
					}
				}
			};

			// Start traversal from the container element
			traverseNodes(element);

			// Cache the result
			this.nodeCache = {
				element,
				contentLength,
				nodes: result
			};

			return result;
		},
		onContentEditableKeyup (event: KeyboardEvent) {
			if (this.checkIsModelSuggestionMenuEditing()) return;

			// Skip special keys that shouldn't trigger the menu
			if (
				[
					'Backspace',
					'Delete',
					'ArrowLeft',
					'ArrowRight',
					'ArrowUp',
					'ArrowDown',
					'Escape',
					'Enter',
					'Tab',
				].includes(event.key)
			) {
				return;
			}

			// Check if @ symbol was pressed and model suggestions are enabled
			if (event.key === '@' && this.enableModelSuggestions) {
				event.preventDefault();

				// Cancel any pending history updates to prevent the @ from being recorded
				this.cancelDebouncedHistoryUpdate();

				// No longer deleting the @ symbol - keep it in place
				// This preserves the text node structure and cursor position
				const selection: Selection = window.getSelection();
				if (selection && selection.rangeCount > 0) {
					const editableElement: HTMLElement = this.$refs.editableContent as HTMLElement;

					// Store the position for later (now includes the @ symbol)
					this.lastAtPosition = this.getCurrentCursorPosition();

					// Get the position for the menu
					const currentRange: Range = selection.getRangeAt(0);
					const rect: DOMRect = currentRange.getBoundingClientRect();
					const editableRect: DOMRect = editableElement.getBoundingClientRect();

					this.menuPosition = {
						x: rect.left - editableRect.left,
						y: rect.bottom - editableRect.top
					};

					// Open the model menu
					this.modelMenuOpen = true;
				}
			}
		},
		onSuggestionMenuClose () {
			// Restore focus to prompt field
			const promptField: HTMLElement = this.$refs.editableContent as HTMLElement;
			if (promptField) {
				promptField.focus();

				// restore cursor position
				if (this.lastAtPosition >= 0) {
					const lastAtPosition: number = this.lastAtPosition; // Save val because we reset it below
					this.$nextTick(() => {
						try {
							this.setCursorPositionInText(promptField, lastAtPosition);
						} catch (e) {
							console.warn('Could not restore cursor position', e);
						}
					});
				}
			}

			// Reset tracking variables
			this.lastAtPosition = -1;
			// Close the model menu
			this.modelMenuOpen = false;
		},
		// Handle model selection
		onSelectModel (model: Model) {
			if (!model || this.lastAtPosition < 0) return;

			// Store the model in our local map for access during rendering
			this.localModelsMap = {
				...this.localModelsMap,
				[model.id]: model,
			};

			// Get current text in original form with tokens as formatted chips
			const editableElement: HTMLElement = this.$refs.editableContent as HTMLElement;

			// Instead of relying on plain text that would convert chips to raw tokens,
			// insert directly at the cursor position in the DOM
			editableElement.focus();
			const selection: Selection = window.getSelection();

			// Create a range and insert our token
			if (selection && selection.rangeCount > 0) {
				// Set selection to the position where @ was typed
				const allNodes: Node[] = this.collectPromptValueNodes(editableElement);
				let currentPosition: number = 0;
				let targetNode: Node | null = null;
				let targetOffset: number = 0;

				// Find the node and offset corresponding to lastAtPosition
				for (const node of allNodes) {
					// Check if we've reached or passed the target position
					if (node.nodeType === Node.TEXT_NODE) {
						const nodeLength: number = node.textContent?.length || 0;

						// If this text node contains our position
						if (currentPosition <= this.lastAtPosition &&
							currentPosition + nodeLength >= this.lastAtPosition) {
							targetNode = node;
							targetOffset = this.lastAtPosition - currentPosition;
							break;
						}

						currentPosition += nodeLength;
					} else if (node.nodeType === Node.ELEMENT_NODE) {
						const el: HTMLElement = node as HTMLElement;

						// Chips and BR/DIV elements count as 1 character
						if (el.classList?.contains('prompt-field-chip') || el.tagName === 'BR' || el.tagName === 'DIV') {
							// Check if our position is exactly at this element
							if (currentPosition === this.lastAtPosition) {
								// Position is right before this element
								if (node.previousSibling && node.previousSibling.nodeType === Node.TEXT_NODE) {
									// Use the end of the previous text node
									targetNode = node.previousSibling;
									targetOffset = node.previousSibling.textContent?.length || 0;
								} else if (node.nextSibling && node.nextSibling.nodeType === Node.TEXT_NODE) {
									// Use the beginning of the next text node
									targetNode = node.nextSibling;
									targetOffset = 0;
								} else {
									// Fallback: Insert a text node before this element
									const textNode: Text = document.createTextNode('');
									node.parentNode.insertBefore(textNode, node);
									targetNode = textNode;
									targetOffset = 0;
								}
								break;
							}

							currentPosition += 1; // Count as 1 character
						}
					}
				}

				if (targetNode) {
					// Set selection at the right position (where @ was before deletion)
					const range: Range = document.createRange();
					range.setStart(targetNode, targetOffset);
					range.setEnd(targetNode, targetOffset);
					selection.removeAllRanges();
					selection.addRange(range);

					// Insert the model token
					// Create a text node with our model token (without the @ symbol because it's already there)
					const partialModelToken: string = `{MODEL-${model.id}}`;
					const textNode: Text = document.createTextNode(partialModelToken);
					selection.getRangeAt(0).insertNode(textNode);

					// Get the updated text and update the model
					let newText: string = this.getPlainTextFromEditable();

					// Replace all existing model tokens with the new model token using the helper method
					newText = this.replaceAllModelTokens(newText, model.id);

					// This will trigger the promptStringValue watcher which will format the tokens
					this.onPromptStringValueInput(newText);
					// Clear caches since content changed
					this.nodeCache = null;
					this.plainTextCache = { text: '', version: 0, lastContentLength: 0 };
				}
			}
		},

		handleGenerateRandomPrompt () {
			this.loadingPrompt = true;

			const randomPromptsArray: string[] = [...localPrompts];
			if (this.promptStringValue) _remove(randomPromptsArray, (pr: string) => pr === this.promptStringValue); // Remove last prompt
			const randomPrompt: string = _sampleSize(randomPromptsArray, 1)[0];

			setTimeout(() => {
				this.onPromptStringValueInput(randomPrompt);
				// Clear caches since content changed
				this.nodeCache = null;
				this.plainTextCache = { text: '', version: 0, lastContentLength: 0 };
				this.loadingPrompt = false;
			}, 250);
		},
		handleClearPrompt () {
			this.onPromptStringValueInput(null);
			// Clear caches since content changed
			this.nodeCache = null;
			this.plainTextCache = { text: '', version: 0, lastContentLength: 0 };
			// Focus on the editable content
			this.$nextTick(() => {
				const editableElement: HTMLElement = this.$refs.editableContent as HTMLElement;
				if (editableElement) {
					editableElement.focus();
					this.setCursorAtEnd(editableElement);
				}
			});
		},
		addToHistory (value: string) {
			const trimmedValue: string = _trim(value);

			// Protect against adding the same value multiple times (e.g. undos and redos)
			if (
				this.historyIndex >= 0 &&
				this.historyIndex <= (this.history.length - 1) &&
				trimmedValue === _trim(this.history[this.historyIndex].text)
			) {
				return;
			}

			// If we're not at the end of history, truncate everything after current position
			if (this.historyIndex < this.history.length - 1) {
				this.history = this.history.slice(0, this.historyIndex + 1);
			}

			// Add new state to history
			if (!this.history.length || this.history[this.history.length - 1].text !== trimmedValue) {
				this.history.push({
					text: trimmedValue,
					cursor: this.getCurrentCursorPosition(),
				});
			}

			// Limit history length
			if (this.history.length > this.maxHistoryLength) {
				this.history = this.history.slice(this.history.length - this.maxHistoryLength);
			}

			// Update current position
			this.historyIndex = this.history.length - 1;
		},
		undo () {
			if (!this.isUndoingRedoing && this.historyIndex > 0) {
				// Cancel any pending debounced history updates
				this.cancelDebouncedHistoryUpdate();

				this.isUndoingRedoing = true;
				this.historyIndex--;
				const historyItem: TextHistoryEntry = this.history[this.historyIndex];
				const value: string = historyItem.text;
				this.onPromptStringValueInput(value);

				// Focus and restore cursor position, with additional delay to ensure DOM is updated
				setTimeout(() => {
					const container: HTMLElement = this.$refs.editableContent as HTMLElement;
					container.focus();
					this.setCursorPositionInText(container, historyItem.cursor);
					this.isUndoingRedoing = false;
				}, 1);
			}
		},
		redo () {
			if (!this.isUndoingRedoing && this.historyIndex < this.history.length - 1) {
				// Cancel any pending debounced history updates
				this.cancelDebouncedHistoryUpdate();

				this.isUndoingRedoing = true;
				this.historyIndex++;
				const historyItem: TextHistoryEntry = this.history[this.historyIndex];
				const value: string = historyItem.text;
				this.onPromptStringValueInput(value);

				// Focus and restore cursor position, with additional delay to ensure DOM is updated
				setTimeout(() => {
					const container: HTMLElement = this.$refs.editableContent as HTMLElement;
					container.focus();
					this.setCursorPositionInText(container, historyItem.cursor);
					this.isUndoingRedoing = false;
				}, 1);
			}
		},
		onDebouncedAddToHistory (value: string) {
			if (!this.debouncedAddToHistory) {
				this.debouncedAddToHistory = _debounce(this.addToHistory, 700);
			}
			this.debouncedAddToHistory(value);
		},
		cancelDebouncedHistoryUpdate () {
			if (this.debouncedAddToHistory && this.debouncedAddToHistory.cancel) {
				this.debouncedAddToHistory.cancel();
			}
		},
		debouncedScrollCheck () {
			if (!this.debouncedCheckForScroll) {
				this.debouncedCheckForScroll = _debounce(() => {
					// Use requestAnimationFrame to avoid blocking the main thread
					requestAnimationFrame(() => {
						this.checkForScroll();
					});
				}, 150); // Debounce for 150ms to reduce frequency
			}
			this.debouncedCheckForScroll();
		},
		cancelDebouncedScrollCheck () {
			if (this.debouncedCheckForScroll && this.debouncedCheckForScroll.cancel) {
				this.debouncedCheckForScroll.cancel();
			}
		},
		setupDebouncedPlainTextExtraction () {
			if (!this.debouncedPlainTextExtraction) {
				this.debouncedPlainTextExtraction = _debounce((plainText: string) => {
					this.onPromptStringValueInput(plainText);
				}, 16); // ~60fps for smooth typing
			}
		},
		cancelDebouncedPlainTextExtraction () {
			if (this.debouncedPlainTextExtraction && this.debouncedPlainTextExtraction.cancel) {
				this.debouncedPlainTextExtraction.cancel();
			}
		},
		setupDebouncedAccuracySync () {
			if (!this.debouncedAccuracySync) {
				this.debouncedAccuracySync = _debounce(() => {
					// Get fresh text from DOM
					const freshText = this.getPlainTextFromEditable();

					// Compare with current prop value
					if (freshText !== this.promptStringValue) {
						// Update parent with accurate data
						this.onPromptStringValueInput(freshText);

						// Update cache to stay in sync
						this.plainTextCache.text = freshText;
						this.plainTextCache.lastContentLength = freshText.length;
						this.plainTextCache.version++;
					}
				}, 300); // Wait 300ms after user stops typing
			}
		},
		cancelDebouncedAccuracySync () {
			if (this.debouncedAccuracySync && this.debouncedAccuracySync.cancel) {
				this.debouncedAccuracySync.cancel();
			}
		},
		calculatePlaceholderHeight () {
			this.$nextTick(() => {
				const measureElement: HTMLElement = this.$refs.placeholderMeasure as HTMLElement;
				if (measureElement) {
					const height = measureElement.offsetHeight;
					this.placeholderMinHeight = height > 0 ? height : null;
				}
			});
		},
		// Format tokens as chips in the editable area
		updateEditableWithFormattedTokens (text: string): void {
			const editableElement: HTMLElement = this.$refs.editableContent as HTMLElement;
			if (!editableElement) return;

			// Check if we have any tokens to format or any newlines
			const tokenRegex: RegExp = new RegExp(this.MODEL_ID_CHIP_REGEX.source, this.MODEL_ID_CHIP_REGEX.flags);
			if (!tokenRegex.test(text) && !text.includes('\n')) {
				editableElement.innerText = text;
				return;
			}

			// Create a document fragment to build the formatted content
			const fragment: DocumentFragment = document.createDocumentFragment();
			let match: RegExpExecArray;

			// Split text by newlines to preserve them
			const lines: string[] = text.split('\n');

			for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
				const line: string = lines[lineIndex];
				let lineLastIndex: number = 0;

				// Process tokens within this line
				// Reset the regex state
				tokenRegex.lastIndex = 0;
				while ((match = tokenRegex.exec(line)) !== null) {
					// Add text before the token
					if (match.index > lineLastIndex) {
						const textBefore: Text = document.createTextNode(line.substring(lineLastIndex, match.index));
						fragment.appendChild(textBefore);
					}

					// Create the chip span for the token
					const tokenSpan: HTMLElement = document.createElement('span');
					tokenSpan.className = 'prompt-field-chip';

					// Extract model ID from token
					const modelId: string = match[1];

					// Check if we have this model in our cache
					if (this.localModelsMap[modelId]) {
						tokenSpan.innerText = this.buildModelTokenLabel(this.localModelsMap[modelId]);
					} else {
						// If not in cache, show loading text
						tokenSpan.innerText = `Loading...`;
					}

					tokenSpan.dataset.originalToken = match[0]; // Store original token for later retrieval
					// Make the chip non-editable
					tokenSpan.contentEditable = 'false';
					// Add a click handler
					tokenSpan.addEventListener('click', (e) => {
						e.preventDefault();
						e.stopPropagation();
					});

					fragment.appendChild(tokenSpan);

					lineLastIndex = match.index + match[0].length;
				}

				// Add any remaining text after the last token in this line
				if (lineLastIndex < line.length) {
					const textAfter: Text = document.createTextNode(line.substring(lineLastIndex));
					fragment.appendChild(textAfter);
				}

				// Add a line break after each line except the last
				if (lineIndex < lines.length - 1) {
					const br: HTMLElement = document.createElement('br');
					fragment.appendChild(br);
				}
			}

			// Clear the editable and append the new content
			editableElement.innerHTML = '';
			editableElement.appendChild(fragment);
		},
		updateEditableContentWithCursorPreservation (value: string): void {
			// Remember cursor position before updating content
			const editableElement: HTMLElement = this.$refs.editableContent as HTMLElement;
			const isElementFocused = editableElement && document.activeElement === editableElement;
			let cursorPosition: number = 0;

			if (isElementFocused) {
				// Only save position if the editable element is focused
				cursorPosition = this.getCurrentCursorPosition();
			}

			// Update content with tokens transformed to chips
			this.updateEditableWithFormattedTokens(value || '');
			// Clear caches since content structure changed
			this.nodeCache = null;
			this.plainTextCache = { text: '', version: 0, lastContentLength: 0 };

			// Restore cursor position after DOM update
			if (isElementFocused) {
				this.$nextTick(() => {
					this.setCursorPositionInText(editableElement, cursorPosition);
				});
			}
		},

		// Get cached plain text for performance optimization
		getCachedPlainText (): string {
			const editableElement: HTMLElement = this.$refs.editableContent as HTMLElement;
			if (!editableElement) return '';

			// Simple cache check based on content length and version
			const currentContentLength = editableElement.textContent?.length || 0;
			const currentVersion = this.plainTextCache.version;

			// For small content (< 50 chars), be more sensitive to changes for immediate feedback
			// Using aggressive caching for performance, debounced accuracy sync ensures eventual accuracy
			const sensitivityThreshold = currentContentLength < 20 ? 1 : 5; // Reverted to original threshold
			const contentLengthChanged = Math.abs(currentContentLength - (this.plainTextCache.lastContentLength || 0)) >= sensitivityThreshold;

			if (contentLengthChanged || !this.plainTextCache.text) {
				// Extract fresh plain text and update cache
				this.plainTextCache.text = this.getPlainTextFromEditable();
				this.plainTextCache.lastContentLength = currentContentLength;
				this.plainTextCache.version++;
			}

			return this.plainTextCache.text;
		},

		// Get plain text from editable content, converting chips back to tokens
		getPlainTextFromEditable (): string {
			const editableElement: HTMLElement = this.$refs.editableContent as HTMLElement;
			if (!editableElement) return '';

			// Clone the element to work with
			const clone: HTMLElement = editableElement.cloneNode(true) as HTMLElement;

			// Find all token chips
			const chips: HTMLElement[] = Array.from(clone.querySelectorAll('.prompt-field-chip')) as HTMLElement[];

			// Replace each chip with its original token value
			chips.forEach((chip: HTMLElement) => {
				const originalToken: string = (chip as HTMLElement).dataset.originalToken;
				if (originalToken) {
					const textNode: Text = document.createTextNode(originalToken);
					chip.parentNode?.replaceChild(textNode, chip);
				}
			});

			// Get text content with proper newline preservation
			// We need to process the DOM to correctly handle line breaks
			let result: string = '';
			const processNode = (node: Node) => {
				if (node.nodeType === Node.TEXT_NODE) {
					result += node.textContent;
				} else if (node.nodeType === Node.ELEMENT_NODE) {
					const element: HTMLElement = node as HTMLElement;
					if (element.tagName === 'BR') {
						result += '\n';
					} else if (element.tagName === 'DIV' && result.length > 0 && !result.endsWith('\n')) {
						// Add newline for div elements if not at start of string and not already newline
						result += '\n';
					}

					// Process child nodes
					for (let i = 0; i < node.childNodes.length; i++) {
						processNode(node.childNodes[i]);
					}
				}
			};

			// Process all nodes
			for (let i = 0; i < clone.childNodes.length; i++) {
				processNode(clone.childNodes[i]);
			}

			return result.replace(/\u200B/g, '');
		},
		onContentEditableCopy (event: ClipboardEvent) {
			if (this.checkIsModelSuggestionMenuEditing()) return;

			// Get selection
			const selection: Selection = window.getSelection();
			if (!selection || selection.rangeCount === 0) return;

			const range: Range = selection.getRangeAt(0);

			// Create a temporary div to hold selected content
			const tempDiv: HTMLElement = document.createElement('div');
			tempDiv.appendChild(range.cloneContents());

			// Find all chips in the selection and replace with original syntax
			const chips: HTMLElement[] = Array.from(tempDiv.querySelectorAll('.prompt-field-chip')) as HTMLElement[];

			// Find all line breaks and make sure they're preserved
			const brs: HTMLElement[] = Array.from(tempDiv.querySelectorAll('br'));
			const divs: HTMLElement[] = Array.from(tempDiv.querySelectorAll('div:not(:first-child)'));

			// Convert BR elements to newline characters
			brs.forEach((br: HTMLElement) => {
				const textNode: Text = document.createTextNode('\n');
				br.parentNode?.replaceChild(textNode, br);
			});

			// Convert DIV elements to include newline characters before them (except the first div)
			divs.forEach((div: HTMLElement) => {
				// Insert a newline before this div
				if (div.parentNode) {
					const newline: Text = document.createTextNode('\n');
					div.parentNode.insertBefore(newline, div);
				}
			});

			// Replace each chip with its original token value
			chips.forEach((chip: HTMLElement) => {
				const originalToken: string = chip.dataset.originalToken;
				if (originalToken) {
					const textNode: Text = document.createTextNode(originalToken);
					chip.parentNode?.replaceChild(textNode, chip);
				}
			});

			// Process the DOM to get text with proper newline preservation
			let formattedText: string = '';
			const processNode = (node: Node) => {
				if (node.nodeType === Node.TEXT_NODE) {
					formattedText += node.textContent;
				} else if (node.nodeType === Node.ELEMENT_NODE) {
					// Process child nodes
					for (let i = 0; i < node.childNodes.length; i++) {
						processNode(node.childNodes[i]);
					}
				}
			};

			// Process all nodes to get the text content
			for (let i = 0; i < tempDiv.childNodes.length; i++) {
				processNode(tempDiv.childNodes[i]);
			}

			// Process the text to ensure proper spacing around tokens
			const tokenRegex: RegExp = new RegExp(this.MODEL_ID_CHIP_REGEX_WITH_WHITESPACE.source, this.MODEL_ID_CHIP_REGEX_WITH_WHITESPACE.flags);
			formattedText = formattedText.replace(tokenRegex, (match: string, spaceBefore: string, tokenContent: string, spaceAfter: string) => (
				// Replace with the token and exactly one space on either side
				` @{MODEL-${tokenContent}} `
			));

			// Trim any excess whitespace from the final result
			formattedText = _trim(formattedText.replace(/\u200B/g, ''));

			// Set the clipboard data with the properly formatted text
			event.clipboardData?.setData('text/plain', formattedText);
			event.preventDefault(); // Prevent the default copy behavior
		},
		onContentEditablePaste (event: ClipboardEvent) {
			if (this.checkIsModelSuggestionMenuEditing()) return;

			// Get plain text from clipboard
			const clipboardData: DataTransfer = event.clipboardData;
			if (!clipboardData) return;

			// Get text only, no colors or formatting
			const plainText: string = clipboardData.getData('text/plain');
			if (!plainText) return;

			// Prevent the default paste with formatting
			event.preventDefault();

			// Check if pasted text contains model tokens
			const tokenRegex: RegExp = new RegExp(this.MODEL_ID_CHIP_REGEX.source, this.MODEL_ID_CHIP_REGEX.flags);
			const hasTokens: boolean = tokenRegex.test(plainText);

			// Get current selection
			const selection: Selection | null = window.getSelection();
			if (!selection || selection.rangeCount === 0) return;

			// Get the range
			const range: Range = selection.getRangeAt(0);

			// Insert the pasted text node
			this.lastPasteTextNode = null;
			const triggerInputHandlerChange = () => {
				const editableElement: HTMLElement = this.$refs.editableContent as HTMLElement;
				if (editableElement) {
					editableElement.dispatchEvent(new Event('input', { bubbles: true }));
				}
			};

			if (hasTokens) {
				// Special handling for text with tokens
				// First insert a space to trigger a DOM update
				const spaceNode: Text = document.createTextNode(' ');
				range.deleteContents();
				range.insertNode(spaceNode);

				// Trigger input event to update model temporarily for a moment
				triggerInputHandlerChange();

				// Then in the next tick, replace with the actual content
				this.$nextTick(() => {
					// Get the space we just inserted
					if (selection.rangeCount > 0) {
						const newRange: Range = selection.getRangeAt(0);
						newRange.selectNode(spaceNode);
						newRange.deleteContents();

						// Insert the actual text
						const textNode: Text = document.createTextNode(plainText);
						this.lastPasteTextNode = textNode;
						newRange.insertNode(textNode);

						// Move cursor to the end
						newRange.setStartAfter(textNode);
						newRange.setEndAfter(textNode);
						selection.removeAllRanges();
						selection.addRange(newRange);

						// Let the input handler process the change
						triggerInputHandlerChange();
					}
				});
			} else {
				// Normal paste for text without tokens
				// Delete any selected content first
				range.deleteContents();

				// Insert the plain text at cursor position
				const textNode: Text = document.createTextNode(plainText);
				this.lastPasteTextNode = textNode;
				range.insertNode(textNode);

				// Move cursor to end of inserted text
				range.setStartAfter(textNode);
				range.setEndAfter(textNode);
				selection.removeAllRanges();
				selection.addRange(range);

				// Let the input handler process the change
				triggerInputHandlerChange();
			}
		},

		/**
		 * If the collapsed cursor is inside a chip (contentEditable=false), move it to after the chip
		 * so the user can type. Fixes Firefox placing the caret inside the chip on click/focus.
		 */
		correctCursorIfInsideChip () {
			const editableElement: HTMLElement = this.$refs.editableContent as HTMLElement;
			if (!editableElement) return;

			const selection: Selection = window.getSelection();
			if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return;
			if (!editableElement.contains(selection.anchorNode)) return;

			const chip: HTMLElement | null = this.findParentChip(selection.anchorNode as Node);
			if (!chip) return;

			const range: Range = document.createRange();
			const next: Node | null = chip.nextSibling;
			if (next && next.nodeType === Node.TEXT_NODE) {
				const len: number = (next.textContent || '').length;
				range.setStart(next, len);
				range.setEnd(next, len);
			} else {
				const zwspNode: Text = document.createTextNode('\u200B');
				chip.parentNode?.insertBefore(zwspNode, chip.nextSibling);
				range.setStart(zwspNode, 1);
				range.setEnd(zwspNode, 1);
			}
			selection.removeAllRanges();
			selection.addRange(range);
		},

		// Handle selection changes to ensure token chips are selected as atomic units
		handleSelectionChange () {
			// Ensure we're only handling selection changes in our component
			if (!this.$refs.editableContent) return;

			const selection: Selection = window.getSelection();
			if (!selection || selection.rangeCount === 0) return;

			// In Firefox, click/focus can put collapsed cursor inside contentEditable=false chip; move it out
			if (selection.isCollapsed) {
				this.correctCursorIfInsideChip();
				return;
			}

			// Check if selection starts or ends inside a token chip
			const range: Range = selection.getRangeAt(0);
			const startContainer: Node = range.startContainer;
			const endContainer: Node = range.endContainer;

			// Find if we're inside a token chip
			const startChip: HTMLElement = this.findParentChip(startContainer);
			const endChip: HTMLElement = this.findParentChip(endContainer);

			// If selection starts or ends inside a chip, adjust it to include the whole chip
			if (startChip || endChip) {
				const newRange: Range = document.createRange();

				// Set start position
				if (startChip) {
					newRange.setStartBefore(startChip);
				} else {
					newRange.setStart(range.startContainer, range.startOffset);
				}

				// Set end position
				if (endChip) {
					newRange.setEndAfter(endChip);
				} else {
					newRange.setEnd(range.endContainer, range.endOffset);
				}

				// Apply the adjusted selection
				selection.removeAllRanges();
				selection.addRange(newRange);
			}
		},

		// Helper to find parent chip element
		findParentChip (node: Node): HTMLElement {
			let current: Node = node;
			while (current) {
				if (current.nodeType === Node.ELEMENT_NODE &&
					(current as HTMLElement).classList &&
					(current as HTMLElement).classList.contains('prompt-field-chip')) {
					return current as HTMLElement;
				}
				current = current.parentNode;
				if (current === this.$refs.editableContent) break;
			}
			return null;
		},
		onContentEditableKeydown (event: KeyboardEvent) {
			if (this.checkIsModelSuggestionMenuEditing()) return;

			if (this.loading) {
				event.preventDefault();
				return false;
			}

			// Handle Shift+Enter - trigger immediate accuracy sync to prevent race conditions with form submission
			if (event.key === 'Enter' && event.shiftKey) {
				// Cancel any pending debounced accuracy sync
				this.cancelDebouncedAccuracySync();

				// Immediately sync with fresh DOM data
				const freshText = this.getPlainTextFromEditable();
				if (freshText !== this.promptStringValue) {
					this.onPromptStringValueInput(freshText);
					// Update cache to stay in sync
					this.plainTextCache.text = freshText;
					this.plainTextCache.lastContentLength = freshText.length;
					this.plainTextCache.version++;
				}

				// Don't prevent default - let the form submission proceed
				return;
			}

			// Handle undo (Ctrl+Z or Cmd+Z)
			if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
				event.preventDefault();
				this.undo();
				return;
			}

			// Handle redo (Ctrl+Shift+Z, Cmd+Shift+Z, or Ctrl+Y)
			if (((event.ctrlKey || event.metaKey) && event.key === 'z' && event.shiftKey) ||
				(event.ctrlKey && event.key === 'y')) {
				event.preventDefault();
				this.redo();
				return;
			}

			// Handle Cmd+RightArrow or Cmd+DownArrow (disable as this is too buggy with chips being at the start or end of a line)
			if ((event.metaKey || event.ctrlKey) && (event.key === 'ArrowRight' || event.key === 'ArrowDown')) {
				event.preventDefault();
				return;
			}

			// Handle Cmd+LeftArrow or Cmd+UpArrow (disable as this is too buggy with chips being at the start or end of a line)
			if ((event.metaKey || event.ctrlKey) && (event.key === 'ArrowLeft' || event.key === 'ArrowUp')) {
				event.preventDefault();
				return;
			}
		},
		buildModelTokenLabel (model: Model) {
			return `@${_truncate(model.name, { length: 22 })}`;
		},
		// Helper method to set cursor position at the end of the text
		setCursorAtEnd (element: HTMLElement) {
			// Make sure we have content
			if (!element.firstChild) {
				element.innerText = '';
				return;
			}

			const range: Range = document.createRange();
			const selection: Selection = window.getSelection();

			// Set range at the end of the content
			try {
				if (element.lastChild?.nodeType === Node.TEXT_NODE) {
					const textNode: Text = element.lastChild as Text;
					range.setStart(textNode, textNode.length);
				} else {
					range.setStartAfter(element.lastChild as Node);
				}
				range.collapse(true);

				selection?.removeAllRanges();
				selection?.addRange(range);
			} catch (e) {
				console.warn('Could not set cursor at end', e);
			}
		},

		// Helper method to set cursor position in the entire text
		setCursorPositionInText (element: HTMLElement, position: number): void {
			// Ensure we have content to work with
			if (!element.firstChild) {
				element.innerText = ' ';
			}

			const selection: Selection = window.getSelection();
			if (!selection) return;

			// Get all nodes including text nodes and chip elements
			const allNodes: Node[] = this.collectPromptValueNodes(element);

			// Walk through nodes, counting positions until we reach our target
			let currentPos: number = 0;
			let targetNode: Node | null = null;
			let targetOffset: number = 0;
			let targetNodeIndex: number = -1;

			for (let i: number = 0; i < allNodes.length; i++) {
				const node: Node = allNodes[i];
				const {
					positionValue,
					hasPreFormattedTokens,
				} = this.calculateNodePositionValue(node, i);

				// Check if target position is within this node
				if (currentPos <= position && currentPos + positionValue >= position) {
					targetNode = node;
					targetNodeIndex = i;

					if (node.nodeType === Node.TEXT_NODE) {
						// For text nodes, calculate the offset within the text
						if (hasPreFormattedTokens) {
							// For text nodes with tokens, don't place cursor inside tokens
							// Just place at beginning of the node for safety
							targetOffset = 0;
						} else {
							// For regular text nodes, use normal offset calculation
							targetOffset = position - currentPos;
						}
					}
					break;
				}

				// Advance the position counter by this node's value
				currentPos += positionValue;
			}

			// If we've gone through all nodes and still haven't found position,
			// set cursor at the end
			if (!targetNode) {
				if (allNodes.length > 0) {
					const lastNode: Node = allNodes[allNodes.length - 1];
					targetNodeIndex = allNodes.length - 1;
					if (lastNode.nodeType === Node.TEXT_NODE) {
						targetNode = lastNode;
						targetOffset = lastNode.textContent?.length || 0;
					} else {
						// Position after the last node
						targetNode = lastNode;
						targetOffset = 1;
					}
				} else {
					// Fallback to element itself
					targetNode = element;
					targetOffset = 0;
				}
			}

			// Set cursor position
			try {
				const range: Range = document.createRange();

				if (
					targetNode.nodeType === Node.ELEMENT_NODE &&
					(targetNode as HTMLElement).classList?.contains('prompt-field-chip')
				) {
					// Position after the node
					range.setStartAfter(targetNode);
					range.setEndAfter(targetNode);
				} else if (
					targetNode.nodeType === Node.ELEMENT_NODE &&
					(
						(targetNode as HTMLElement).tagName === 'BR' ||
						(targetNode as HTMLElement).tagName === 'DIV'
					)
				) {
					// Position before the node
					range.setStartBefore(targetNode);
					range.setEndBefore(targetNode);
				} else {
					range.setStart(targetNode, targetOffset);
					range.setEnd(targetNode, targetOffset);
				}

				selection.removeAllRanges();
				selection.addRange(range);
			} catch (e) {
				console.warn('Failed to set cursor position', e);
				// Fallback to end of content
				this.setCursorAtEnd(element);
			}
		},
		async fetchMissingModels (modelIds: string[]): Promise<{ errorIds: string[] }> {
			const errorIds: string[] = [];

			try {
				const uniqueModelIds: string[] = _uniq(modelIds || []);
				for (const modelId of uniqueModelIds) {
					// Skip if we already have this model
					if (this.localModelsMap[modelId]) continue;

					try {
						// Create a reference to the model document
						const modelRef = db.doc(`models/${modelId}`);
						const modelSnapshot = await modelRef.get();

						if (modelSnapshot.exists) {
							// Create the model object with data and id
							const modelData: Model = modelSnapshot.data() as Model;

							// Check if model is deleted
							if (modelData.isDeleted) {
								console.warn(`Model with ID ${modelId} is marked as deleted`);
								errorIds.push(modelId);
								continue;
							}

							// Fetch avatar if the model has an avatarId
							let avatar = modelData.avatar;
							if (modelData.avatarId) {
								try {
									const avatarDoc = await db.doc(`avatars/${modelData.avatarId}`).get();
									if (avatarDoc.exists) {
										avatar = {
											...avatarDoc.data(),
											id: modelData.avatarId
										} as Avatar;
									}
								} catch (error) {
									console.warn(`Failed to fetch avatar for model ${modelId}:`, error);
								}
							}

							const model: Model = {
								...modelData,
								avatar,
								get id () { return modelSnapshot.id },
							} as Model;

							// Update the localModelsMap with this model
							this.localModelsMap = {
								...this.localModelsMap,
								[modelId]: model,
							};
						} else {
							console.warn(`Model with ID ${modelId} not found`);
							errorIds.push(modelId);
						}
					} catch (error) {
						console.error(`Error fetching model ${modelId}:`, error);
						errorIds.push(modelId);
					}
				}
			} catch (error) {
				console.error('Error in fetchMissingModels:', error);
			}

			return { errorIds };
		},
		updatePromptTextWithNewModel (model: Model) {
			// Get current text
			let text: string = this.promptStringValue || '';

			// Check if we have any model tokens already
			const allTokensRegex: RegExp = new RegExp(this.MODEL_ID_CHIP_REGEX.source, this.MODEL_ID_CHIP_REGEX.flags);

			// If model is falsey, remove all model tokens
			if (!model) {
				if (allTokensRegex.test(text)) {
					// Remove all model tokens
					text = text.replace(allTokensRegex, '').trim();
					this.onPromptStringValueInput(text);
				}
				return;
			}

			// Store the model in our local map for access during rendering
			this.localModelsMap = {
				...this.localModelsMap,
				[model.id]: model,
			};

			// If we have models already, replace them all with the new one
			if (allTokensRegex.test(text)) {
				// Replace ALL model tokens with the new token using the helper method
				text = this.replaceAllModelTokens(text, model.id);

				// Update the prompt
				this.onPromptStringValueInput(text);
			} else if (text) {
				// If there's text but no model tokens, add one at the beginning
				this.onPromptStringValueInput(`@{MODEL-${model.id}} ${text}`);
			} else {
				// If there's no text, just add the model token
				this.onPromptStringValueInput(`@{MODEL-${model.id}}`);
			}
		},
		// Helper method to replace all model tokens with a specific model token
		replaceAllModelTokens (text: string, modelId: string): string {
			if (!text) return text;

			// Create token regex
			const regex: RegExp = new RegExp(this.MODEL_ID_CHIP_REGEX.source, this.MODEL_ID_CHIP_REGEX.flags);
			const fullModelToken: string = `@{MODEL-${modelId}}`;

			// Replace ALL model tokens with the new token
			return text.replace(regex, () => fullModelToken);
		},
		onPromptExpandToggle () {
			this.promptExpanded = !this.promptExpanded;
			this.$emit('expand-prompt', this.promptExpanded);
		},
		checkForScroll () {
			const editableElement: HTMLElement = this.$refs.editableContent as HTMLElement;
			if (editableElement) {
				const hasScroll = editableElement.scrollHeight > editableElement.clientHeight;
				// Only update if the scroll state actually changed to prevent unnecessary reactivity
				if (this.hasScrollContent !== hasScroll) {
					this.hasScrollContent = hasScroll;
				}
			}
		},
		triggerUpload (close: Function) {
			// Trigger the hidden ImageUpload component's file dialog
			if (this.$refs.imageUpload) {
				this.$refs.imageUpload.openFileDialog();
			}
			close();
		},
		openLibrary (imageGalleryDialogInitialNav: ImageGalleryDialogNav, close: Function) {
			this.updateRunnitState({
				imageGalleryDialogOpen: true,
				imageGalleryDialogInitialNav: imageGalleryDialogInitialNav,
			});
			this.enterSelectionMode(NODE_RUN_RESULT_SELECTION_MODE.SINGLE, 'prompt-image-selection', this.field, ['IMG']);
			close();
		},
		handleImageUpload (upload: any) {
			if (upload.nodeRunResult) {
				this.generatePromptFromImage({ nodeRunResult: upload.nodeRunResult });
			} else if (upload.upload) {
				this.generatePromptFromImage({ upload: upload.upload });
			} else {
				this.generatePromptFromImage({ upload: upload });
			}
		},
		async generatePromptFromImage (imageInput: { nodeRunResult?: RunnitNodeRunResult, upload?: RunnitUpload }) {
			if (this.loadingPrompt) return;

			let generatedPrompts = [];
			this.loadingPrompt = true;
			try {
				const functionRef = functions
					.httpsCallable('promptMaker');

				// Prepare the parameters object
				const params: any = {
					prompt: '', // Note: this works pretty well with  combining instructions in the prompt with the image. It felt like unexpected behaivior so we don't send the prompt. this.promptStringValue || '',
					numGenerations: 1,
				};

				// Add image data based on what we have
				if (imageInput.nodeRunResult) {
					params.nodeRunResults = [imageInput.nodeRunResult];
				}
				if (imageInput.upload) {
					params.uploads = [imageInput.upload];
				}

				const { success, generations, error } = (await functionRef(params)).data;

				if (success && generations && generations.length) {
					generatedPrompts = generations;
				} else if (!success) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: error || 'Error enhancing prompt, please try again',
						show: true,
					});
				}
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error enhancing prompt, please try again',
					show: true,
				});
			} finally {
				this.loadingPrompt = false;

				// Apply the generated prompt to the prompt field
				if (generatedPrompts && generatedPrompts.length > 0) {
					const generatedPrompt = generatedPrompts[0];

					// Update the prompt with the generated positive prompt
					if (generatedPrompt.positive) {
						this.onPromptStringValueInput(generatedPrompt.positive);
					}
				}
			}
		},
	},
	components: {
		RunnitSettingHelperBtn,
		RunnitDynamicFieldLabelRow,
		PromptMagicButton,
		ModelSuggestionMenu,
		MenuOrBottomSheet,
		ListItem,
		ImageUpload,
	},
});
